/**
 * Skeleton — squelettes de chargement (L13.15).
 *
 * Remplace les « Chargement… » en texte brut et les spinners isolés par des
 * silhouettes animées qui reprennent la forme du contenu attendu. La perception
 * de vitesse est meilleure (le layout ne « saute » pas à l'arrivée des données).
 *
 * S'appuie sur l'utilitaire `.shimmer` déjà défini dans styles/globals.css
 * (fond bg-surface-hi + dégradé animé `animate-shimmer`). Le respect de
 * `prefers-reduced-motion` est déjà géré globalement (globals.css §reduced).
 *
 * Composants :
 *   - <Skeleton/>      : bloc nu, dimensionné via className (h-*, w-*)
 *   - <SkeletonText/>  : n lignes de texte (dernière ligne raccourcie)
 *   - <SkeletonRow/>   : silhouette d'une ligne de liste (icône + texte + chevron)
 *   - <SkeletonList/>  : n SkeletonRow empilés (état de chargement d'une liste)
 *   - <SkeletonCard/>  : silhouette d'une carte glass (titre + lignes)
 *
 * Accessibilité : le conteneur porte `role="status"` + `aria-label` pour
 * annoncer le chargement aux lecteurs d'écran, et `aria-hidden` sur les blocs
 * décoratifs internes.
 */

import { clsx } from 'clsx';
import type { HTMLAttributes } from 'react';

// ----------------------------------------------------------------------------
// Bloc de base
// ----------------------------------------------------------------------------

interface SkeletonProps extends HTMLAttributes<HTMLDivElement> {
  /** Forme du bloc. `block` (défaut) = coins arrondis r-md, `circle` = rond, `pill` = arrondi pill. */
  shape?: 'block' | 'circle' | 'pill';
}

/**
 * Bloc squelette nu. On le dimensionne avec les classes Tailwind habituelles :
 *   <Skeleton className="h-4 w-32" />
 *   <Skeleton shape="circle" className="h-10 w-10" />
 */
export function Skeleton({ shape = 'block', className, ...props }: SkeletonProps) {
  return (
    <div
      aria-hidden="true"
      className={clsx(
        'shimmer',
        shape === 'circle' && 'rounded-full',
        shape === 'pill' && 'rounded-r-pill',
        shape === 'block' && 'rounded-r-md',
        className,
      )}
      {...props}
    />
  );
}

// ----------------------------------------------------------------------------
// Texte multi-lignes
// ----------------------------------------------------------------------------

interface SkeletonTextProps {
  /** Nombre de lignes (défaut 3). */
  lines?: number;
  className?: string;
}

/** Plusieurs lignes de texte ; la dernière est raccourcie pour le réalisme. */
export function SkeletonText({ lines = 3, className }: SkeletonTextProps) {
  return (
    <div className={clsx('space-y-2', className)} aria-hidden="true">
      {Array.from({ length: lines }).map((_, i) => (
        <Skeleton
          key={i}
          className={clsx('h-3.5', i === lines - 1 ? 'w-2/3' : 'w-full')}
        />
      ))}
    </div>
  );
}

// ----------------------------------------------------------------------------
// Ligne de liste
// ----------------------------------------------------------------------------

/**
 * Silhouette d'une ligne de liste interactive (cf. SignalementRow) :
 * icône d'urgence à gauche, deux lignes de texte, chevron à droite.
 */
export function SkeletonRow({ className }: { className?: string }) {
  return (
    <div
      aria-hidden="true"
      className={clsx(
        'flex items-center gap-3 rounded-r-xl border border-border bg-surface/40',
        'backdrop-blur-glass px-4 py-3 shadow-soft',
        className,
      )}
    >
      <Skeleton shape="circle" className="h-5 w-5 shrink-0" />
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex gap-2">
          <Skeleton shape="pill" className="h-4 w-16" />
          <Skeleton shape="pill" className="h-4 w-20" />
        </div>
        <Skeleton className="h-3.5 w-3/4" />
        <Skeleton className="h-3 w-1/2" />
      </div>
      <Skeleton className="h-4 w-4 shrink-0" />
    </div>
  );
}

interface SkeletonListProps {
  /** Nombre de lignes fantômes (défaut 5). */
  count?: number;
  className?: string;
  /** Libellé annoncé aux lecteurs d'écran. */
  label?: string;
}

/** Liste de SkeletonRow — état de chargement d'une page liste. */
export function SkeletonList({ count = 5, className, label = 'Chargement de la liste' }: SkeletonListProps) {
  return (
    <div role="status" aria-label={label} className={clsx('space-y-2', className)}>
      {Array.from({ length: count }).map((_, i) => (
        <SkeletonRow key={i} />
      ))}
      <span className="sr-only">{label}…</span>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Carte glass
// ----------------------------------------------------------------------------

interface SkeletonCardProps {
  /** Nombre de lignes de texte dans le corps (défaut 4). */
  lines?: number;
  /** Affiche une silhouette de titre en haut. Défaut true. */
  withTitle?: boolean;
  className?: string;
  label?: string;
}

/** Silhouette d'une carte glass (titre + lignes) — état de chargement d'une fiche. */
export function SkeletonCard({
  lines = 4,
  withTitle = true,
  className,
  label = 'Chargement du contenu',
}: SkeletonCardProps) {
  return (
    <div
      role="status"
      aria-label={label}
      className={clsx(
        'rounded-r-2xl border border-border bg-surface/40 backdrop-blur-glass p-5 shadow-soft',
        className,
      )}
    >
      {withTitle && (
        <div className="mb-4 flex items-center gap-3" aria-hidden="true">
          <Skeleton shape="circle" className="h-10 w-10 shrink-0" />
          <div className="flex-1 space-y-2">
            <Skeleton className="h-3 w-24" />
            <Skeleton className="h-4 w-2/5" />
          </div>
        </div>
      )}
      <SkeletonText lines={lines} />
      <span className="sr-only">{label}…</span>
    </div>
  );
}
